<template>
  <div>
    <UserCenterLayout
      :side-menus="sideMenus"
      :active-menu-key="activeMenuKey"
      :page-width="999"
      :page-height="0"
      :page-min-height="1526"
      page-padding="23px 30px 38px"
      page-overflow="visible"
      :sidebar-height="0"
      :sidebar-min-height="1465"
      :main-width="757"
      :main-height="0"
      :main-scrollable="false"
      :header-height="55"
      header-justify="center"
      @side-menu-click="handleSideMenu"
    >
      <button class="back-button" type="button" @click="goUserCenter">
        <img :src="userIcons.back" alt="">
        返回
      </button>

      <div class="page-title-block">
        <h1>申请发票</h1>
        <p>请选择需要开具发票的订单并填写开票信息，提交后我们将尽快为您开具并发送</p>
      </div>

      <div class="invoice-content">
        <div class="form-column">
          <section class="invoice-card order-select-card">
            <div class="step-title">
              <span>1</span>
              选择要开发票的订单
            </div>
            <InvoiceOrderCard
              v-if="hasInvoiceableOrders"
              :order="selectedOrder"
              selected
              @select="selectOrder"
              @copy="copyOrderNo"
            ></InvoiceOrderCard>
            <div v-else class="empty-order-tip">暂无可开票订单</div>
            <button v-if="invoiceableOrders.length > 1" class="more-order-button" type="button" @click="toggleOrderList">
              选择其他订单
              <img :src="userIcons.down" alt="">
            </button>
            <div v-if="showOrderList && invoiceableOrders.length > 1" class="order-dropdown">
              <InvoiceOrderCard
                v-for="item in invoiceableOrders"
                :key="item.orderNo"
                mode="option"
                :order="item"
                :selected="item.orderNo === selectedOrder.orderNo"
                @select="selectOrder"
                @copy="copyOrderNo"
              ></InvoiceOrderCard>
            </div>
          </section>

          <section class="invoice-card">
            <div class="step-title">
              <span>2</span>
              发票类型
            </div>
            <div class="option-grid">
              <button
                v-for="item in invoiceTypes"
                :key="item.value"
                class="type-option"
                type="button"
                :class="{ active: form.invoiceType === item.value, disabled: isInvoiceTypeDisabled(item.value) }"
                @click="handleInvoiceTypeChange(item.value)"
              >
                <strong>{{ item.label }}</strong>
                <em>{{ item.desc }}</em>
              </button>
            </div>
          </section>

          <section class="invoice-card title-type-card">
            <div class="step-title">
              <span>3</span>
              抬头类型
            </div>
            <div class="radio-row">
              <BaseRadio
                v-for="item in titleTypes"
                :key="item.value"
                v-model="form.titleType"
                class="title-radio"
                :option-value="item.value"
                name="invoice-title-type"
                @change="handleTitleTypeChange"
              >
                {{ item.label }}
              </BaseRadio>
            </div>
          </section>

          <section class="invoice-card form-card">
            <div class="step-title">
              <span>4</span>
              发票信息
            </div>
            <label class="form-field">
              <span>发票抬头 <i>*</i></span>
              <input v-model.trim="form.title" type="text" :placeholder="titlePlaceholder">
            </label>
            <label v-if="form.titleType === 'enterprise'" class="form-field">
              <span>纳税人识别号 <i>*</i></span>
              <input v-model.trim="form.taxpayerId" type="text" placeholder="请输入纳税人识别号（统一社会信用代码）">
            </label>
            <label class="form-field">
              <span>接收邮箱 <i>*</i></span>
              <input v-model.trim="form.email" type="text" placeholder="用于接收电子发票">
            </label>
            <label class="form-field">
              <span>手机号码</span>
              <input v-model.trim="form.mobile" type="text" placeholder="选填，便于接收发票通知">
            </label>
          </section>

          <section class="invoice-card amount-card">
            <div class="step-title">
              <span>5</span>
              开票金额
            </div>
            <div class="amount-panel">
              <span>可开票金额（含税）</span>
              <strong>{{ selectedOrder.amountText }}</strong>
              <em>订单实际支付金额</em>
            </div>
            <label class="form-field remark-field">
              <span>备注（选填）</span>
              <textarea v-model.trim="form.remark" placeholder="如有特殊说明，请在此填写"></textarea>
            </label>
          </section>

          <button class="submit-button" type="button" :disabled="submitting" @click="handleSubmit">
            {{ submitting ? '提交中...' : '提交申请' }}
          </button>
          <p class="privacy-tip">
            <img :src="userIcons.safe" alt="">
            您的信息仅用于开票服务，我们将严格保密
          </p>

          <section class="invoice-record-card">
            <div>
              <h2>
                <img :src="userIcons.tip" alt="">
                发票记录
              </h2>
              <p>可在发票管理中查看历史发票申请及开具状态</p>
            </div>
            <button type="button" @click="handleRecordEntry">
              前往查看
              <img :src="userIcons.right" alt="">
            </button>
          </section>
        </div>

        <aside class="side-column">
          <section class="order-info-card">
            <h2>订单信息</h2>
            <dl>
              <dt>订单号</dt>
              <dd>
                {{ selectedOrder.orderNo }}
                <button type="button" aria-label="复制订单编号" @click="copyOrderNo">
                  <img :src="userIcons.copy" alt="">
                </button>
              </dd>
              <dt>套餐名称</dt>
              <dd>{{ selectedOrder.packageName }}</dd>
              <dt>支付方式</dt>
              <dd class="pay-method">
                <img :src="selectedOrder.raw.payment_provider === 'alipay' ? userIcons.alipay : userIcons.wechat" alt="">
                {{ selectedOrder.paymentMethod }}
              </dd>
              <dt>支付时间</dt>
              <dd>{{ selectedOrder.paidAt }}</dd>
            </dl>
            <div class="amount-summary">
              <span>发票金额</span>
              <strong>{{ selectedOrder.amountText }}</strong>
              <p>发票金额将与实际支付一致</p>
            </div>
          </section>

          <section class="notice-card">
            <h2>
              <img :src="userIcons.tip" alt="">
              温馨提示
            </h2>
            <p>电子发票一般在 1-2 个工作日内开具</p>
            <p>电子发票将发送至您填写的邮箱</p>
            <p>如需帮助请联系客服</p>
          </section>
        </aside>
      </div>
    </UserCenterLayout>

  </div>
</template>

<script>
import auth from '@/store/auth'
import userCenter from '@/store/userCenter'
import BaseRadio from '@/components/BaseRadio.vue'
import InvoiceOrderCard from '@/components/InvoiceOrderCard.vue'
import UserCenterLayout from '@/layouts/UserCenterLayout.vue'
import { getInvoiceableOrders, getInvoiceRecords, submitInvoiceApplication } from '@/services/invoice'
import copyIcon from '@/assets/images/user/copy.svg'
import rightIcon from '@/assets/images/user/right.svg'
import tipIcon from '@/assets/images/user/tip.svg'
import backIcon from '@/assets/images/icons/back.svg'
import downIcon from '@/assets/images/icons/down.svg'
import safeIcon from '@/assets/images/icons/protect.svg'
import wechatIcon from '@/assets/images/icons/wechat-green.svg'
import alipayIcon from '@/assets/images/icons/zfb.svg'

// 常量：左侧导航与个人中心保持一致，发票页默认高亮发票管理
const SIDE_MENUS = [
  { key: 'profile', label: '用户信息' },
  // 关键节点：充值中心属于独立页面，点击后通过路由离开发票视图
  { key: 'recharge', label: '充值中心', routeName: 'recharge-center' },
  { key: 'plan', label: '当前套餐' },
  { key: 'status', label: '多端状态' },
  { key: 'invoice', label: '发票管理' },
  { key: 'orders', label: '订单记录' }
]

// 常量：页面图标统一入口，缺失视觉资源时仍保留 img 标签位置
const USER_ICONS = {
  copy: copyIcon,
  right: rightIcon,
  tip: tipIcon,
  back: backIcon,
  down: downIcon,
  safe: safeIcon,
  wechat: wechatIcon,
  alipay: alipayIcon
}

// 常量：发票类型选项，字段值与提交参数保持一致
const INVOICE_TYPES = [
  { value: 'normal', label: '普通发票', desc: '个人/企业均可开具' },
  { value: 'special', label: '专用发票', desc: '仅限企业抬头' }
]

// 常量：抬头类型选项，切换时会联动专票可用性
const TITLE_TYPES = [
  { value: 'enterprise', label: '企业' },
  { value: 'personal', label: '个人' }
]

// 常量：企业发票抬头校验规则，限制长度并过滤不支持的特殊字符
const COMPANY_REGEX = /^[\u4e00-\u9fa5a-zA-Z0-9（）().·]{4,100}$/

// 常量：个人发票抬头校验规则，仅允许中文姓名及姓名间的间隔号
const PERSONAL_REGEX = /^[\u4e00-\u9fa5]{2,15}(·[\u4e00-\u9fa5]{2,15})*$/

// 常量：发票表单默认值，后端字段提供后保持该结构对接
const DEFAULT_FORM = {
  invoiceType: 'normal',
  titleType: 'enterprise',
  title: '',
  taxpayerId: '',
  email: '',
  mobile: '',
  remark: ''
}

// 常量：无订单时的页面兜底数据，避免接口异常导致模板空引用
const EMPTY_ORDER = {
  id: '',
  orderNo: '--',
  packageName: '--',
  paidAt: '--',
  paymentMethod: '--',
  amount: 0,
  amountCents: 0,
  amountText: '¥0.00',
  status: '--'
}

// 常量：后端发票类型枚举当前按抬头类型区分
const INVOICE_TYPE_MAP = {
  enterprise: 'COMPANY',
  personal: 'PERSONAL'
}

export default {
  name: 'InvoiceApplyView',
  components: { BaseRadio, InvoiceOrderCard, UserCenterLayout },
  props: {
    // 常量参数：从个人中心带入的订单号，用于打开申请发票时预选订单
    initialOrderNo: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      activeMenuKey: 'invoice',
      sideMenus: SIDE_MENUS,
      userIcons: USER_ICONS,
      invoiceTypes: INVOICE_TYPES,
      titleTypes: TITLE_TYPES,
      invoiceableOrders: [],
      selectedOrder: EMPTY_ORDER,
      showOrderList: false,
      submitting: false,
      form: {
        ...DEFAULT_FORM
      }
    }
  },
  computed: {
    // 方法：判断当前是否存在可开票订单，用于控制订单卡片和空态分支
    hasInvoiceableOrders() {
      return this.invoiceableOrders.length > 0
    },
    // 方法：根据抬头类型展示不同输入提示
    titlePlaceholder() {
      return this.form.titleType === 'enterprise' ? '请输入公司名称' : '请输入个人姓名'
    }
  },
  mounted() {
    // 逻辑分支：未登录打开申请发票视图时，先唤起登录弹窗
    if (!auth.isLoggedIn()) {
      auth.openLoginDialog(() => {
        this.loadInvoiceableOrders()
      })
      return
    }

    this.loadInvoiceableOrders()
  },
  watch: {
    // 关键节点：抽屉重复打开申请发票时，更新当前订单预选
    initialOrderNo(orderNo) {
      this.selectInitialOrder(orderNo)
    }
  },
  methods: {
    // 方法：加载可开票订单，并根据抽屉传入的 orderNo 预选订单
    loadInvoiceableOrders() {
      getInvoiceableOrders()
        .then((orders) => {
          this.invoiceableOrders = Array.isArray(orders) ? orders : []
          this.selectInitialOrder(this.initialOrderNo)
          this.showOrderList = false
        })
        .catch((error) => {
          this.invoiceableOrders = []
          this.selectedOrder = EMPTY_ORDER
          this.showOrderList = false
          this.$toast(error.message || '可开票订单加载失败')
        })
    },
    // 方法：按抽屉传入的订单号选择订单，未命中时回落到第一条订单
    selectInitialOrder(orderNo) {
      const matchedOrder = this.invoiceableOrders.find(item => item.orderNo === orderNo)
      this.selectedOrder = matchedOrder || this.invoiceableOrders[0] || EMPTY_ORDER
    },
    // 方法：左侧导航返回个人中心对应模块，当前页发票管理保持本页
    handleSideMenu(item) {
      // 逻辑分支：充值中心是独立路由，先关闭抽屉再跳转，避免抽屉遮挡目标页面
      if (item.routeName) {
        userCenter.close()
        this.$router.push({ name: item.routeName, query: { source: 'account' } })
        return
      }

      if (item.key === 'invoice') {
        return
      }

      this.$emit('navigate-section', item.key)
    },
    // 方法：返回个人中心发票管理区域
    goUserCenter() {
      this.$emit('back-to-overview', 'invoice')
    },
    // 方法：切换其他订单下拉区域
    toggleOrderList() {
      // 逻辑分支：只有一个或没有可开票订单时，不展示订单切换列表
      if (this.invoiceableOrders.length <= 1) {
        return
      }

      this.showOrderList = !this.showOrderList
    },
    // 方法：选择当前要申请发票的订单
    selectOrder(order) {
      if (!order) {
        return
      }

      this.selectedOrder = order
      this.showOrderList = false
    },
    // 方法：切换抬头类型，个人抬头不保留企业税号
    handleTitleTypeChange(type) {
      this.form.titleType = type

      // 逻辑分支：专用发票仅限企业抬头，切换个人时自动回到普通发票
      if (type === 'personal') {
        this.form.invoiceType = 'normal'
        this.form.taxpayerId = ''
      }
    },
    // 方法：判断指定发票类型在当前抬头类型下是否不可选
    isInvoiceTypeDisabled(type) {
      return this.form.titleType === 'personal' && type === 'special'
    },
    // 方法：切换发票类型，专票在个人抬头下保持不可选
    handleInvoiceTypeChange(type) {
      if (this.isInvoiceTypeDisabled(type)) {
        this.$toast('专用发票仅支持企业抬头')
        return
      }

      this.form.invoiceType = type
    },
    // 方法：复制当前订单号，浏览器不支持剪贴板时静默降级
    copyOrderNo(order = this.selectedOrder) {
      const targetOrder = order && order.orderNo ? order : this.selectedOrder

      if (navigator.clipboard && targetOrder.orderNo !== EMPTY_ORDER.orderNo) {
        navigator.clipboard.writeText(targetOrder.orderNo)
      }

      this.$toast('已复制订单编号')
    },
    // 方法：校验必填项，返回第一条错误文案
    validateForm() {
      if (!this.selectedOrder.id || this.selectedOrder.amountCents <= 0) {
        return '暂无可申请发票的订单'
      }

      if (!this.form.title) {
        return '请填写发票抬头'
      }

      // 逻辑分支：企业和个人抬头使用不同格式规则，避免提交不符合开票要求的名称
      if (this.form.titleType === 'enterprise' && !COMPANY_REGEX.test(this.form.title)) {
        return '企业抬头需为4-100位，仅支持中文、英文、数字、括号、点号和间隔号'
      }

      if (this.form.titleType === 'personal' && !PERSONAL_REGEX.test(this.form.title)) {
        return '个人抬头需为2-15位中文姓名，可使用间隔号分隔多个姓名'
      }

      if (this.form.titleType === 'enterprise' && !this.form.taxpayerId) {
        return '请填写纳税人识别号'
      }

      if (!this.form.email) {
        return '请填写接收邮箱'
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        return '请填写正确的邮箱地址'
      }

      return ''
    },
    // 方法：组装提交参数，字段名与后端发票申请接口保持一致
    buildSubmitPayload() {
      return {
        order_id: this.selectedOrder.id,
        invoice_type: INVOICE_TYPE_MAP[this.form.titleType],
        title: this.form.title,
        tax_id: this.form.titleType === 'enterprise' ? this.form.taxpayerId : undefined,
        email: this.form.email,
        amount_cents: this.selectedOrder.amountCents
      }
    },
    // 方法：提交发票申请，失败时展示平台错误信息
    handleSubmit() {
      const errorMessage = this.validateForm()
      if (errorMessage) {
        this.$toast(errorMessage)
        return
      }

      this.submitting = true
      submitInvoiceApplication(this.buildSubmitPayload())
        .then(() => {
          this.$toast('发票申请已提交')
        })
        .catch((error) => {
          this.$toast(error.message || '发票申请提交失败')
        })
        .finally(() => {
          this.submitting = false
        })
    },
    // 方法：查询发票记录，当前页面暂无记录列表时展示查询结果数量
    handleRecordEntry() {
      getInvoiceRecords()
        .then((records) => {
          this.$toast(`已查询到 ${records.length} 条发票记录`)
        })
        .catch((error) => {
          this.$toast(error.message || '发票记录查询失败')
        })
    }
  }
}
</script>

<style scoped lang="less">
.back-button{
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #666;
  font-size: 14px;
  line-height: 24px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  img{
    width: 14px;
    height: 14px;
    margin-right: 8px;
  }
}

.page-title-block{
  margin: 28px 0 12px 31px;
  h1{
    margin: 0;
    color: #000000D9;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
  }
  p{
    margin: 6px 0 0;
    color: #999;
    font-size: 12px;
    line-height: 18px;
  }
}

.invoice-content{
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.form-column{
  width: 548px;
}

.side-column{
  width: 195px;
}

.invoice-card,
.order-info-card,
.notice-card,
.invoice-record-card{
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(31, 65, 129, 0.07);
  box-sizing: border-box;
}

.invoice-card{
  margin-bottom: 12px;
  padding: 16px 30px 24px;
}

.step-title{
  color: #000000D9;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  display: flex;
  align-items: center;
  span{
    width: 20px;
    height: 20px;
    margin-right: 14px;
    border-radius: 50%;
    background: #3A83FF;
    color: #fff;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }
}

.order-select-card{
  position: relative;
  padding-bottom: 14px;
}

.more-order-button{
  height: 24px;
  margin: 18px auto 0;
  border: none;
  background: transparent;
  color: #2F7BFF;
  font-size: 12px;
  line-height: 24px;
  display: flex;
  align-items: center;
  cursor: pointer;
  img{
    width: 12px;
    height: 12px;
    margin-left: 8px;
  }
}

.order-dropdown{
  position: absolute;
  left: 30px;
  right: 30px;
  top: 156px;
  z-index: 5;
  padding: 6px;
  border: 1px solid #DCE8FF;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 10px 22px rgba(31, 65, 129, 0.12);
}

.empty-order-tip{
  width: 486px;
  min-height: 100px;
  margin-top: 15px;
  border: 1px dashed #C8D9F5;
  border-radius: 6px;
  background: #F7FAFF;
  color: #8A96A8;
  font-size: 14px;
  line-height: 100px;
  text-align: center;
  box-sizing: border-box;
}

.option-grid{
  margin-top: 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.type-option{
  height: 70px;
  border: 1px solid #D9D9D9;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  text-align: center;

  &.active{
    border-color: #8EBBFF;
    background: #EAF3FF;
  }

  &.disabled{
    cursor: not-allowed;
    opacity: 0.48;
  }

  strong,
  em{
    display: block;
  }

  strong{
    color: #333;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
  }

  em{
    margin-top: 2px;
    color: #666;
    font-size: 12px;
    line-height: 18px;
    font-style: normal;
  }
}

.title-type-card{
  padding-bottom: 22px;
}

.radio-row{
  margin-top: 22px;
  display: flex;
  align-items: center;
  gap: 42px;

  .title-radio{
    color: #333;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
  }
}

.form-card{
  padding-bottom: 26px;
}

.form-field{
  margin-top: 16px;
  display: block;

  span{
    margin-bottom: 9px;
    color: #333;
    font-size: 12px;
    line-height: 18px;
    display: block;
    i{
      color: #FF4D4F;
      font-style: normal;
    }
  }

  input,
  textarea{
    width: 100%;
    border: 1px solid #D9D9D9;
    border-radius: 4px;
    background: #fff;
    color: #333;
    font-size: 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder{
      color: #BFBFBF;
    }

    &:focus{
      border-color: #8EBBFF;
    }
  }

  input{
    height: 28px;
    padding: 0 12px;
  }

  textarea{
    height: 79px;
    padding: 12px;
    resize: none;
  }
}

.amount-card{
  padding-bottom: 24px;
}

.amount-panel{
  height: 82px;
  margin-top: 16px;
  padding: 12px 30px;
  border: 1px solid #8EBBFF;
  border-radius: 6px;
  background: #EAF3FF;
  position: relative;
  box-sizing: border-box;

  span{
    color: #333;
    font-size: 12px;
    line-height: 18px;
  }

  strong{
    margin-top: 15px;
    color: #333;
    font-size: 22px;
    line-height: 28px;
    font-weight: 600;
    display: block;
  }

  em{
    position: absolute;
    right: 30px;
    bottom: 17px;
    color: #333;
    font-size: 12px;
    line-height: 18px;
    font-style: normal;
  }
}

.remark-field{
  margin-top: 20px;
}

.submit-button{
  width: 548px;
  height: 38px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(91deg, #6AD0F8 0%, #4C85FF 54%, #71C4F8 100%);
  color: #fff;
  font-size: 12px;
  line-height: 38px;
  cursor: pointer;

  &:disabled{
    cursor: not-allowed;
    opacity: 0.72;
  }
}

.privacy-tip{
  margin: 10px 0 18px;
  color: #999;
  font-size: 10px;
  line-height: 18px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  img{
    width: 12px;
    height: 12px;
    margin-right: 6px;
  }
}

.invoice-record-card{
  height: 80px;
  padding: 17px 30px;
  background: #EAF3FF;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2{
    margin: 0;
    color: #333;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    display: flex;
    align-items: center;
    img{
      width: 16px;
      height: 16px;
      margin-right: 8px;
    }
  }

  p{
    margin: 6px 0 0;
    color: #666;
    font-size: 12px;
    line-height: 18px;
  }

  button{
    border: none;
    background: transparent;
    color: #2F7BFF;
    font-size: 12px;
    line-height: 18px;
    display: flex;
    align-items: center;
    cursor: pointer;
    img{
      width: 12px;
      height: 12px;
      margin-left: 6px;
    }
  }
}

.order-info-card{
  padding: 17px 21px 19px;
  min-height: 367px;

  h2{
    margin: 0 0 22px;
    color: #000000D9;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
  }

  dl{
    margin: 0;
    padding-bottom: 17px;
    border-bottom: 1px solid #E5E5E5;
  }

  dt{
    margin-top: 14px;
    color: #333;
    font-size: 12px;
    line-height: 18px;
    font-weight: 600;

    &:first-child{
      margin-top: 0;
    }
  }

  dd{
    margin: 4px 0 0;
    color: #666;
    font-size: 12px;
    line-height: 18px;
    word-break: break-all;

    button{
      width: 16px;
      height: 16px;
      margin-left: 4px;
      padding: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      vertical-align: middle;
      img{
        width: 12px;
        height: 12px;
      }
    }
  }
}

.pay-method{
  display: flex;
  align-items: center;
  img{
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }
}

.amount-summary{
  padding-top: 15px;
  span{
    color: #333;
    font-size: 12px;
    line-height: 18px;
  }
  strong{
    margin-top: 6px;
    color: #2F7BFF;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    display: block;
  }
  p{
    margin: 10px 0 0;
    color: #999;
    font-size: 12px;
    line-height: 18px;
  }
}

.notice-card{
  margin-top: 12px;
  padding: 18px 30px 16px;
  background: #EAF3FF;

  h2{
    margin: 0 0 14px;
    color: #2F7BFF;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    display: flex;
    align-items: center;
    img{
      width: 16px;
      height: 16px;
      margin-right: 8px;
    }
  }

  p{
    margin: 0 0 12px;
    color: #666;
    font-size: 12px;
    line-height: 18px;

    &::before{
      content: '·';
      margin-right: 4px;
    }
  }
}

</style>
